import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import { authService } from '../services/authService';

interface ProfilePageProps {
  onBack: () => void;
}

const ProfilePage: React.FC<ProfilePageProps> = ({ onBack }) => {
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      setError('');

      try {
        const profile = await authService.getMyProfile();
        setUserName(profile.userName || '');
        setEmail(profile.email || '');
        setPhoneNumber(profile.phoneNumber || '');
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Nem sikerült betölteni a felhasználói adatokat');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await authService.updateMyProfile({
        userName: userName.trim(),
        email: email.trim(),
        phoneNumber: phoneNumber.trim(),
      });

      setSuccessMessage(response.message);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Nem sikerült menteni a profil adatokat');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      return;
    }

    setSavingPassword(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await authService.changePassword({
        currentPassword,
        newPassword,
      });

      setSuccessMessage(response.message);
      setCurrentPassword('');
      setNewPassword('');
    } catch (passwordError) {
      setError(passwordError instanceof Error ? passwordError.message : 'Nem sikerült megváltoztatni a jelszót');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        backgroundColor: '#f0f3f6',
        padding: 2,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
      }}
    >
      <Card sx={{ backgroundColor: 'white', maxWidth: 640, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Felhasználói profil
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {successMessage && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  {successMessage}
                </Alert>
              )}

              <Typography variant="h6" gutterBottom>
                Felhasználó adatai
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Név"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                />

                <TextField
                  fullWidth
                  label="Email cím"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />

                <TextField
                  fullWidth
                  label="Telefonszám"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
              </Box>

              <Button
                variant="contained"
                onClick={handleSaveProfile}
                disabled={savingProfile}
                sx={{
                  backgroundColor: '#3a4651',
                  '&:hover': {
                    backgroundColor: '#2d3640',
                  },
                  mb: 3,
                }}
              >
                {savingProfile ? 'Mentés...' : 'Mentés'}
              </Button>

              <Divider sx={{ my: 3, mx: 2, borderColor: '#d3d3d3' }} />

              <Typography variant="h6" gutterBottom>
                Jelszó megváltoztatás
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Régi jelszó"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />

                <TextField
                  fullWidth
                  label="Új jelszó"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />

                <Button
                  variant="contained"
                  onClick={handleChangePassword}
                  disabled={savingPassword || !currentPassword || !newPassword}
                  sx={{
                    backgroundColor: '#3a4651',
                    '&:hover': {
                      backgroundColor: '#2d3640',
                    },
                    width: 'fit-content',
                  }}
                >
                  {savingPassword ? 'Mentés...' : 'Mentés'}
                </Button>
              </Box>
            </>
          )}

          <Divider sx={{ my: 3 }} />

          <Button
            fullWidth
            variant="text"
            onClick={onBack}
            sx={{ color: '#3a4651' }}
          >
            Vissza a főoldalra
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProfilePage;
